import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
  Divider,
  Chip,
  alpha,
  CircularProgress,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import { palette, typography, theme as appTheme } from '../../theme/colors';
import type { CustomerNote } from '../../types';

interface CustomerNotesPanelProps {
  customerId: string;
  notes: CustomerNote[];
  onAddNote: (note: string, isInternal: boolean) => Promise<void>;
  onDeleteNote: (noteId: string) => Promise<void>;
  isAdding?: boolean;
  isDeleting?: string | null;
}

export const CustomerNotesPanel = ({
  customerId,
  notes,
  onAddNote,
  onDeleteNote,
  isAdding = false,
  isDeleting = null,
}: CustomerNotesPanelProps) => {
  const muiTheme = useTheme();
  const mode = muiTheme.palette.mode;
  const [newNote, setNewNote] = useState('');
  const [isInternal, setIsInternal] = useState(false);

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    surfaceHover: palette.surfaceHover,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    surfaceHover: palette.light.surfaceHover,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    await onAddNote(newNote, isInternal);
    setNewNote('');
    setIsInternal(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStaffName = (note: CustomerNote) => {
    if (note.staff) {
      return `${note.staff.firstName} ${note.staff.lastName}`;
    }
    return 'Unknown Staff';
  };

  return (
    <Box>
      {/* Add New Note Form */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: appTheme.radius.lg,
          mb: 3,
        }}
      >
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
              color: colors.textPrimary,
              mb: 2,
            }}
          >
            Add New Note
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            placeholder="Enter note..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            disabled={isAdding}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.surfaceElevated,
                borderRadius: appTheme.radius.md,
                fontSize: typography.fontSize.sm,
                transition: appTheme.transitions.fast,
                '& fieldset': {
                  borderColor: colors.border,
                },
                '&:hover fieldset': {
                  borderColor: colors.borderLight,
                },
                '&.Mui-focused fieldset': {
                  borderColor: palette.primary,
                  borderWidth: '1px',
                },
              },
              '& .MuiOutlinedInput-input': {
                color: colors.textPrimary,
                '&::placeholder': {
                  color: colors.textMuted,
                  opacity: 1,
                },
              },
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isInternal}
                  onChange={(e) => setIsInternal(e.target.checked)}
                  disabled={isAdding}
                  sx={{
                    color: colors.textMuted,
                    '&.Mui-checked': {
                      color: palette.primary,
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
                  Internal note (staff only)
                </Typography>
              }
            />

            <Button
              variant="contained"
              startIcon={isAdding ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <AddIcon />}
              onClick={handleAddNote}
              disabled={!newNote.trim() || isAdding}
              sx={{
                backgroundColor: palette.primary,
                color: '#fff',
                textTransform: 'none',
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.medium,
                borderRadius: appTheme.radius.md,
                px: 3,
                py: 1,
                transition: appTheme.transitions.fast,
                '&:hover': {
                  backgroundColor: palette.primaryDark,
                },
                '&.Mui-disabled': {
                  backgroundColor: alpha(palette.primary, 0.3),
                  color: alpha('#fff', 0.5),
                },
              }}
            >
              {isAdding ? 'Adding...' : 'Add Note'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Notes List */}
      <Box>
        <Typography
          variant="h6"
          sx={{
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.semibold,
            color: colors.textPrimary,
            mb: 2,
          }}
        >
          Notes History ({notes.length})
        </Typography>

        {notes.length === 0 ? (
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: appTheme.radius.lg,
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ color: colors.textMuted, fontSize: typography.fontSize.sm }}>
              No notes yet. Add a note to track customer interactions.
            </Typography>
          </Card>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {notes.map((note) => (
              <Card
                key={note.id}
                sx={{
                  backgroundColor: colors.surface,
                  border: `1px solid ${colors.border}`,
                  borderRadius: appTheme.radius.lg,
                  transition: appTheme.transitions.fast,
                  '&:hover': {
                    borderColor: colors.borderLight,
                    backgroundColor: colors.surfaceHover,
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <PersonIcon sx={{ color: colors.textMuted, fontSize: 18 }} />
                      <Typography
                        sx={{
                          fontSize: typography.fontSize.sm,
                          fontWeight: typography.fontWeight.medium,
                          color: colors.textPrimary,
                        }}
                      >
                        {getStaffName(note)}
                      </Typography>
                      {note.isInternal && (
                        <Chip
                          label="Internal"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            backgroundColor: alpha(palette.accent, 0.15),
                            color: palette.accent,
                            fontWeight: typography.fontWeight.medium,
                          }}
                        />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        sx={{
                          fontSize: typography.fontSize.xs,
                          color: colors.textMuted,
                        }}
                      >
                        {formatDate(note.createdAt)}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => onDeleteNote(note.id)}
                        disabled={isDeleting === note.id}
                        sx={{
                          color: colors.textMuted,
                          transition: appTheme.transitions.fast,
                          '&:hover': {
                            color: palette.error,
                            backgroundColor: alpha(palette.error, 0.1),
                          },
                        }}
                      >
                        {isDeleting === note.id ? (
                          <CircularProgress size={16} sx={{ color: colors.textMuted }} />
                        ) : (
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1.5, borderColor: colors.border }} />

                  <Typography
                    sx={{
                      fontSize: typography.fontSize.sm,
                      color: colors.textSecondary,
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {note.note}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CustomerNotesPanel;
